export const tooltip = {
  selector: '.v-component--tooltip',
  reactive: true,
  instances: new Map(),

  run(el, data, root) {
    if (el.__tooltipInitialized) return;
    el.__tooltipInitialized = true;

    const instance = this.createTooltip(el, data, root);
    this.instances.set(el, instance);

    const observer = new MutationObserver(() => {
      if (!document.contains(el)) {
        this.destroyTooltip(el);
        observer.disconnect();
      }
    });
    observer.observe(document, { childList: true, subtree: true });
  },

  createTooltip(el, data, root) {
    const config = {
      content: el.getAttribute('data-tooltip') || '',
      html: el.hasAttribute('data-tooltip-html'),
      position: el.getAttribute('data-tooltip-position') || 'top',
      trigger: el.getAttribute('data-tooltip-trigger') || 'hover focus',
      delay: parseInt(el.getAttribute('data-tooltip-delay')) || 0,
      maxWidth: parseInt(el.getAttribute('data-tooltip-max-width')) || 300,
      theme: el.getAttribute('data-tooltip-theme') || 'light',
      arrow: el.hasAttribute('data-tooltip-arrow') ?
        el.getAttribute('data-tooltip-arrow') !== 'false' : true,
      sticky: el.hasAttribute('data-tooltip-sticky'),
      interactive: el.hasAttribute('data-tooltip-interactive'),
      followCursor: el.hasAttribute('data-tooltip-follow'),
      aria: el.getAttribute('data-tooltip-aria') || 'describedby',
      showOnCreate: el.hasAttribute('data-tooltip-show'),
      hideOnClick: el.getAttribute('data-tooltip-hide-on-click') !== 'false',
      closeButton: el.hasAttribute('data-tooltip-close-button'),
    };

    const tooltipEl = document.createElement('div');
    tooltipEl.className = `tooltip tooltip-${config.theme}`;
    tooltipEl.setAttribute('role', 'tooltip');
    tooltipEl.setAttribute('aria-hidden', 'true');
    tooltipEl.style.maxWidth = `${config.maxWidth}px`;

    if (config.arrow) {
      const arrowEl = document.createElement('div');
      arrowEl.className = 'tooltip-arrow';
      tooltipEl.appendChild(arrowEl);
    }

    if (config.closeButton) {
      const closeBtn = document.createElement('button');
      closeBtn.className = 'tooltip-close';
      closeBtn.innerHTML = '&times;';
      closeBtn.setAttribute('aria-label', 'Close tooltip');
      tooltipEl.appendChild(closeBtn);
    }

    const contentEl = document.createElement('div');
    contentEl.className = 'tooltip-content';
    config.html ? contentEl.innerHTML = config.content : contentEl.textContent = config.content;
    tooltipEl.appendChild(contentEl);
    document.body.appendChild(tooltipEl);

    const tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;
    tooltipEl.id = tooltipId;
    if (config.aria === 'describedby') {
      el.setAttribute('aria-describedby', tooltipId);
    } else if (config.aria === 'labelledby') {
      el.setAttribute('aria-labelledby', tooltipId);
    }

    let isVisible = false;
    let showTimeout = null;
    let hideTimeout = null;
    let popperInstance = null;
    let lastMousePosition = null;

    const positionTooltip = () => {
      if (config.followCursor && lastMousePosition) {
        tooltipEl.style.left = `${lastMousePosition.x}px`;
        tooltipEl.style.top = `${lastMousePosition.y}px`;
        return;
      }

      if (window.Popper && !popperInstance) {
        popperInstance = window.Popper.createPopper(el, tooltipEl, {
          placement: config.position,
          strategy: 'fixed',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
                altBoundary: true,
              },
            },
            {
              name: 'flip',
              options: {
                padding: 8,
              },
            },
          ],
        });
      }
    };

    const show = () => {
      if (isVisible) return;
      clearTimeout(hideTimeout);

      showTimeout = setTimeout(() => {
        tooltipEl.style.display = 'block';
        tooltipEl.setAttribute('aria-hidden', 'false');
        positionTooltip();
        isVisible = true;

        const liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.style.position = 'absolute';
        liveRegion.style.overflow = 'hidden';
        liveRegion.style.clip = 'rect(0 0 0 0)';
        liveRegion.style.height = '1px';
        liveRegion.style.width = '1px';
        liveRegion.style.margin = '-1px';
        liveRegion.style.padding = '0';
        liveRegion.style.border = '0';
        liveRegion.textContent = config.content;
        document.body.appendChild(liveRegion);
        setTimeout(() => liveRegion.remove(), 100);

        if (popperInstance) popperInstance.update();
      }, config.delay);
    };

    const hide = () => {
      if (!isVisible) return;
      clearTimeout(showTimeout);

      hideTimeout = setTimeout(() => {
        tooltipEl.style.display = 'none';
        tooltipEl.setAttribute('aria-hidden', 'true');
        isVisible = false;

        if (popperInstance && !config.sticky) {
          popperInstance.destroy();
          popperInstance = null;
        }
      }, config.delay);
    };

    const toggle = () => {
      isVisible ? hide() : show();
    };

    const triggers = config.trigger.split(' ');

    if (triggers.includes('hover')) {
      el.addEventListener('mouseenter', show);
      el.addEventListener('mouseleave', hide);
      if (config.interactive) {
        tooltipEl.addEventListener('mouseenter', show);
        tooltipEl.addEventListener('mouseleave', hide);
      }
    }

    if (triggers.includes('focus')) {
      el.addEventListener('focus', show);
      el.addEventListener('blur', hide);
    }

    if (triggers.includes('click')) {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        toggle();
      });
    }

    if (config.followCursor) {
      el.addEventListener('mousemove', (e) => {
        lastMousePosition = { x: e.clientX, y: e.clientY };
        if (isVisible) positionTooltip();
      });
    }

    if (triggers.includes('viewport')) {
      const intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            show();
          } else {
            hide();
          }
        });
      }, {
        threshold: 0.1,
      });

      intersectionObserver.observe(el);
      config._intersectionObserver = intersectionObserver;
    }

    if (config.hideOnClick) {
      document.addEventListener('click', (e) => {
        if (isVisible && !el.contains(e.target) && !tooltipEl.contains(e.target)) {
          hide();
        }
      });
    }

    const closeBtn = tooltipEl.querySelector('.tooltip-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', hide);
    }

    document.addEventListener('keydown', (e) => {
      if (isVisible && e.key === 'Escape') hide();
    });

    if (config.showOnCreate) show();

    return {
      show,
      hide,
      toggle,
      updateContent: (newContent) => {
        config.content = newContent;
        config.html ? contentEl.innerHTML = newContent : contentEl.textContent = newContent;
        if (popperInstance) popperInstance.update();
      },
      updatePosition: (newPosition) => {
        config.position = newPosition;
        if (popperInstance) {
          popperInstance.setOptions({ placement: newPosition });
          popperInstance.update();
        }
      },
      destroy: () => {
        el.removeEventListener('mouseenter', show);
        el.removeEventListener('mouseleave', hide);
        el.removeEventListener('focus', show);
        el.removeEventListener('blur', hide);
        tooltipEl.removeEventListener('mouseenter', show);
        tooltipEl.removeEventListener('mouseleave', hide);
        document.removeEventListener('click', hide);
        document.removeEventListener('keydown', hide);

        if (closeBtn) closeBtn.removeEventListener('click', hide);
        if (config._intersectionObserver) config._intersectionObserver.disconnect();
        if (popperInstance) popperInstance.destroy();

        tooltipEl.remove();
      },
    };
  },

  destroyTooltip(el) {
    const instance = this.instances.get(el);
    if (instance) {
      instance.destroy();
      this.instances.delete(el);
    }
  },

  show(el) {
    const instance = this.instances.get(el);
    if (instance) instance.show();
  },

  hide(el) {
    const instance = this.instances.get(el);
    if (instance) instance.hide();
  },

  toggle(el) {
    const instance = this.instances.get(el);
    if (instance) instance.toggle();
  },

  updateContent(el, content) {
    const instance = this.instances.get(el);
    if (instance) instance.updateContent(content);
  },

  updatePosition(el, position) {
    const instance = this.instances.get(el);
    if (instance) instance.updatePosition(position);
  },

  destroyAll() {
    this.instances.forEach((instance, el) => instance.destroy());
    this.instances.clear();
  },
};
